import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";

import { useAuth } from "../contexts/AuthContext";
import { cn } from "../lib/utils";
import ApiKeysTab from "../components/settings/ApiKeysTab";
import AuditLogTab from "../components/settings/AuditLogTab";
import BandwidthThresholdsTab from "../components/settings/BandwidthThresholdsTab";
import CatalogTab from "../components/settings/CatalogTab";
import CompaniesTab from "../components/settings/CompaniesTab";
import CustomReportsTab from "../components/settings/CustomReportsTab";
import CustomRolesTab from "../components/settings/CustomRolesTab";
import ItemsTab from "../components/settings/ItemsTab";
import ManualProfilesTab from "../components/settings/ManualProfilesTab";
import MailSettingsTab from "../components/settings/MailSettingsTab";
import NotificationsTab from "../components/settings/NotificationsTab";
import PricesTab from "../components/settings/PricesTab";
import ReportDestinationsTab from "../components/settings/ReportDestinationsTab";
import RequestersTab from "../components/settings/RequestersTab";
import RolesPermissionsTab from "../components/settings/RolesPermissionsTab";
import SystemSettingsTab from "../components/settings/SystemSettingsTab";
import UsersTab from "../components/settings/UsersTab";
import WebhooksTab from "../components/settings/WebhooksTab";
import AdminSmartboxesPage from "./AdminSmartboxesPage";

/**
 * Settings — one tabbed page for every authenticated user. The URL
 * (`/settings/:tab`) drives the active tab so old `/admin/*` bookmarks can
 * redirect straight into the matching tab (see App.tsx).
 *
 * Every user sees Notifications; admin tabs are capability-gated (and, for
 * the Audit Log, platform-only). A tab the user can't see never renders and
 * its URL falls back to the first visible tab. `notifications` has no gate,
 * so `visibleTabs` is never empty.
 */
interface TabDef {
  key: string;
  label: string;
  cap?: string;
  platformOnly?: boolean;
  node: React.ReactNode;
}

const TABS: TabDef[] = [
  { key: "notifications", label: "Notifications", node: <NotificationsTab /> },
  { key: "users", label: "Users", cap: "users.manage", node: <UsersTab /> },
  { key: "roles", label: "Roles & Permissions", cap: "users.manage", node: <RolesPermissionsTab /> },
  { key: "custom-roles", label: "Custom Roles", cap: "users.manage", node: <CustomRolesTab /> },
  { key: "companies", label: "Companies", cap: "companies.manage", node: <CompaniesTab /> },
  { key: "requesters", label: "Requesters", cap: "companies.manage", node: <RequestersTab /> },
  { key: "catalog", label: "Catalog", node: <CatalogTab /> },
  { key: "items", label: "Items", cap: "tags.manage", node: <ItemsTab /> },
  { key: "prices", label: "Prices", cap: "users.manage", node: <PricesTab /> },
  { key: "smartboxes", label: "SmartBoxes", cap: "users.manage", node: <AdminSmartboxesPage /> },
  { key: "mail", label: "Mail & Notifications", cap: "users.manage", node: <MailSettingsTab /> },
  { key: "system", label: "System", cap: "users.manage", platformOnly: true, node: <SystemSettingsTab /> },
  { key: "manual-profiles", label: "Manual Checkout Forms", cap: "users.manage", platformOnly: true, node: <ManualProfilesTab /> },
  { key: "custom-reports", label: "Custom Reports", cap: "users.manage", platformOnly: true, node: <CustomReportsTab /> },
  { key: "report-destinations", label: "Report Destinations", cap: "users.manage", node: <ReportDestinationsTab /> },
  { key: "api-keys", label: "API Keys", cap: "users.manage", node: <ApiKeysTab /> },
  { key: "webhooks", label: "Webhooks", cap: "users.manage", node: <WebhooksTab /> },
  { key: "bandwidth-thresholds", label: "Bandwidth Thresholds", cap: "users.manage", node: <BandwidthThresholdsTab /> },
  { key: "audit", label: "Audit Log", cap: "users.manage", platformOnly: true, node: <AuditLogTab /> },
];

export const SettingsPage: React.FC = () => {
  const { tab } = useParams<{ tab?: string }>();
  const { can, role } = useAuth();

  const visibleTabs = TABS.filter(
    (t) => (!t.cap || can(t.cap)) && (!t.platformOnly || role === "platform"),
  );

  const active = visibleTabs.find((t) => t.key === tab);
  if (!active) {
    return <Navigate to={`/settings/${visibleTabs[0].key}`} replace />;
  }

  return (
    <div className="p-4 md:p-6">
      <h1 className="text-xl font-bold mb-4">Settings</h1>

      {/* Tab bar — wraps on narrow viewports; ~15 tabs. */}
      <nav className="flex flex-wrap gap-1 border-b border-secondary-200 mb-4">
        {visibleTabs.map((t) => {
          const isActive = t.key === active.key;
          return (
            <Link
              key={t.key}
              to={`/settings/${t.key}`}
              className={cn(
                "px-3 py-2 text-sm rounded-t-md -mb-px border-b-2 transition-colors",
                isActive
                  ? "border-primary-500 text-primary-700 font-semibold bg-white"
                  : "border-transparent text-secondary-600 hover:text-secondary-900 hover:bg-secondary-50",
              )}
            >
              {t.label}
            </Link>
          );
        })}
      </nav>

      <section>
        <h2 className="text-base font-semibold mb-3">{active.label}</h2>
        {active.node}
      </section>
    </div>
  );
};

export default SettingsPage;
